import { useMemo, useState } from 'react'
import { FileText, Table2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

type PreviewMode = 'table' | 'text'

const CSV_EXTENSIONS: Array<string> = ['.csv', '.tsv']
const SHEET_EXTENSIONS: Array<string> = ['.xlsx', '.xlsm', '.xls']
const DELIMITER_CANDIDATES: Array<string> = [',', '\t', ';', '|']
/** Rows past this point are dropped from the DOM; the raw text view still has them. */
const MAX_RENDERED_ROWS = 500

/**
 * How a given document's extracted text encodes its grid.
 *
 * A spreadsheet arrives already flattened by the extractor into tab-joined
 * rows with no quoting, so its shape is known rather than sniffed — and a `"`
 * in a cell is a literal quote, not a field wrapper.
 */
export interface TabularFormat {
  unitNoun: 'page' | 'sheet'
  delimiter?: string
  quoted: boolean
}

interface TabularDocumentPreviewProps {
  title: string
  pageNumber: number
  format: TabularFormat
  extractedText?: string | null
  className?: string
}

export function describeTabularDocument(
  mimeType?: string | null,
  filename?: string | null,
): TabularFormat | null {
  const mime = (mimeType || '').toLowerCase()
  const name = (filename || '').toLowerCase()

  const isSheet =
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    SHEET_EXTENSIONS.some((extension) => name.endsWith(extension))
  if (isSheet) {
    return { unitNoun: 'sheet', delimiter: '\t', quoted: false }
  }

  const isCsv =
    mime === 'text/csv' ||
    mime === 'text/tab-separated-values' ||
    CSV_EXTENSIONS.some((extension) => name.endsWith(extension))
  if (isCsv) {
    return { unitNoun: 'page', quoted: true }
  }

  return null
}

/**
 * Picks the delimiter that appears most often outside quoted fields in the
 * first record. Sniffing beats trusting the extension: exports named `.csv`
 * are routinely semicolon- or tab-separated depending on the source locale.
 */
function detectDelimiter(text: string): string {
  const counts = DELIMITER_CANDIDATES.map((delimiter) => {
    let inQuotes = false
    let count = 0
    for (let i = 0; i < text.length; i += 1) {
      const char = text[i]
      if (char === '"') {
        inQuotes = !inQuotes
        continue
      }
      if (inQuotes) {
        continue
      }
      if (char === '\n') {
        break
      }
      if (char === delimiter) {
        count += 1
      }
    }
    return { delimiter, count }
  })

  const best = counts.reduce((winner, candidate) =>
    candidate.count > winner.count ? candidate : winner,
  )
  return best.count > 0 ? best.delimiter : ','
}

/**
 * Splits delimited text into rows and cells.
 *
 * With `quoted` (the CSV default) this is an RFC 4180 parse: quoted fields may
 * contain the delimiter, newlines, and doubled quotes (`""`) as an escaped
 * quote. A naive `split` would shred the quoted `"USDA, Economic Research
 * Service..."` cells these exports are full of. With `quoted: false` every
 * quote is literal text, which is what already-flattened spreadsheet rows need.
 */
export function parseDelimitedText(
  text: string,
  options?: { delimiter?: string; quoted?: boolean },
): Array<Array<string>> {
  const source = text.replace(/^\uFEFF/, '')
  if (!source.trim()) {
    return []
  }

  const sep = options?.delimiter ?? detectDelimiter(source)
  const honorQuotes = options?.quoted ?? true
  const rows: Array<Array<string>> = []
  let row: Array<string> = []
  let field = ''
  let inQuotes = false

  const endField = () => {
    row.push(field)
    field = ''
  }
  const endRow = () => {
    endField()
    rows.push(row)
    row = []
  }

  for (let i = 0; i < source.length; i += 1) {
    const char = source[i]

    if (inQuotes) {
      if (char === '"') {
        if (source[i + 1] === '"') {
          field += '"'
          i += 1
        } else {
          inQuotes = false
        }
      } else {
        field += char
      }
      continue
    }

    if (char === '"' && honorQuotes) {
      inQuotes = true
    } else if (char === sep) {
      endField()
    } else if (char === '\n') {
      endRow()
    } else if (char !== '\r') {
      field += char
    }
  }

  if (field.length > 0 || row.length > 0) {
    endRow()
  }

  // Trailing newline leaves a single empty cell behind — not a real record.
  const last = rows[rows.length - 1]
  if (last && last.length === 1 && last[0] === '') {
    rows.pop()
  }

  return rows
}

/**
 * A CSV or spreadsheet page rendered as a grid instead of as one long
 * line-wrapped string.
 *
 * Parsing happens client-side from the already-extracted text, so there is no
 * extra request and the table is exactly the content the model reads. The raw
 * text view stays one click away for rows the grid truncates or misaligns.
 */
export function TabularDocumentPreview(props: TabularDocumentPreviewProps) {
  const { title, pageNumber, format, extractedText, className } = props

  const [mode, setMode] = useState<PreviewMode>('table')

  const rows = useMemo(
    () =>
      parseDelimitedText(extractedText || '', {
        delimiter: format.delimiter,
        quoted: format.quoted,
      }),
    [extractedText, format.delimiter, format.quoted],
  )

  const header = rows[0] ?? []
  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0)
  // A single column is not a grid worth showing. This is also the safety net for
  // documents extracted before the pipeline preserved column delimiters.
  const hasTable = rows.length > 1 && columnCount > 1
  const showText = mode === 'text' || !hasTable
  const bodyRows = rows.slice(1, MAX_RENDERED_ROWS + 1)
  const hiddenRowCount = Math.max(0, rows.length - 1 - bodyRows.length)

  return (
    <div
      className={cn('flex min-h-0 min-w-0 flex-1 flex-col gap-3', className)}
      data-testid="tabular-document-preview"
    >
      <div className="flex shrink-0 items-center justify-between gap-3">
        <Text size="xs" color="muted">
          {showText
            ? 'Text as the model reads it'
            : `${rows.length - 1} rows · ${columnCount} columns`}
        </Text>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant={showText ? 'ghost' : 'outline'}
            size="sm"
            className="h-8"
            disabled={!hasTable}
            data-testid="tabular-document-mode-table"
            onClick={() => setMode('table')}
          >
            <Table2 className="mr-1.5 h-3.5 w-3.5" />
            Table
          </Button>
          <Button
            type="button"
            variant={showText ? 'outline' : 'ghost'}
            size="sm"
            className="h-8"
            data-testid="tabular-document-mode-text"
            onClick={() => setMode('text')}
          >
            <FileText className="mr-1.5 h-3.5 w-3.5" />
            Raw text
          </Button>
        </div>
      </div>

      {showText ? (
        <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-border bg-white p-6 text-black shadow-sm dark:bg-zinc-100">
          <pre
            className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-zinc-800"
            data-testid="tabular-document-text"
          >
            {extractedText ||
              `No text extracted for this ${format.unitNoun}.`}
          </pre>
        </div>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-border bg-white shadow-sm dark:bg-zinc-100">
          <div className="min-h-0 min-w-0 flex-1 overflow-auto">
            <table
              className="w-max min-w-full border-collapse text-left text-xs text-zinc-800"
              data-testid="tabular-document-table"
            >
              <thead className="sticky top-0 z-10 bg-zinc-100 dark:bg-zinc-200">
                <tr>
                  <th className="w-10 border-b border-r border-zinc-300 px-2 py-2 text-right font-medium text-zinc-500">
                    #
                  </th>
                  {Array.from({ length: columnCount }, (_, index) => (
                    <th
                      key={index}
                      className="whitespace-nowrap border-b border-r border-zinc-300 px-3 py-2 font-semibold text-zinc-700"
                    >
                      {header[index] || ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-zinc-50">
                    <td className="border-b border-r border-zinc-200 px-2 py-1.5 text-right tabular-nums text-zinc-400">
                      {rowIndex + 1}
                    </td>
                    {Array.from({ length: columnCount }, (_, cellIndex) => (
                      <td
                        key={cellIndex}
                        className="max-w-[22rem] truncate border-b border-r border-zinc-200 px-3 py-1.5 align-top"
                        title={row[cellIndex] || ''}
                      >
                        {row[cellIndex] || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex shrink-0 items-center justify-between border-t border-zinc-200 px-3 py-2 text-[11px] text-zinc-500">
            <span>{title}</span>
            <span>
              {hiddenRowCount > 0
                ? `Showing first ${MAX_RENDERED_ROWS} rows — ${hiddenRowCount} more in raw text`
                : `${format.unitNoun} ${pageNumber}`}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
